import React, { useState, useEffect } from "react";
import { Avatar, Box } from "reusable";
import { useAlerts } from "../../../backend";
import { functions, httpsCallable } from "../../../config/firebase-config";
import { Loader } from "../../components/loader";
import "./get-in-touch.css";

interface GetInTouchModalProps {
  isOpen: boolean;
  onClose: () => void;
  profileOwnerId?: string;
  visitorId?: string;
  isPremium?: boolean;
  profilePicture?: string;
  ownerName?: string;
}

const initialForm = {
  fullName: "",
  contactNumber: "+91-",
  email: "",
  requirement: "",
};

const GetInTouchModal: React.FC<GetInTouchModalProps> = ({ isOpen, onClose, profileOwnerId, profilePicture, ownerName }) => {
  const { addAlert } = useAlerts();
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  // Debug logging when modal opens
  useEffect(() => {
    if (isOpen) {
      console.log("GetInTouchModal opened with:", {
        profileOwnerId,
        ownerName,
        hasProfilePicture: Boolean(profilePicture)
      });
    }
  }, [isOpen, profileOwnerId, ownerName, profilePicture]);

  const handleField = (field: keyof typeof initialForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [field]: e.target.value });
  };

  const handleSubmit = async (e: { preventDefault: () => void }) => {
    e.preventDefault();
    setSubmitting(true);

    // Validate required fields before making the request
    if (!profileOwnerId || profileOwnerId.trim() === '') {
      console.error("Missing profileOwnerId:", profileOwnerId);
      addAlert({ type: "error", message: "Profile owner information is missing. Please try again." });
      setSubmitting(false);
      return;
    }

    if (!form.requirement || form.requirement.trim() === '') {
      console.error("Missing message:", form.requirement);
      addAlert({ type: "error", message: "Please enter your message before submitting." });
      setSubmitting(false);
      return;
    }

    const formData = {
      recipientUserId: profileOwnerId,
      senderName: form.fullName,
      senderEmail: form.email,
      contactNumber: form.contactNumber, // send contact number
      message: form.requirement,
    };

    // Debug logging
    console.log("Submitting contact request with data:", {
      recipientUserId: formData.recipientUserId,
      hasMessage: Boolean(formData.message),
      messageLength: formData.message?.length || 0,
      senderName: formData.senderName,
      senderEmail: formData.senderEmail
    });

    try {
      const handleContactRequest = httpsCallable<typeof formData, { success: boolean; message: string }>(functions, "handleContactRequest");
      const response = await handleContactRequest(formData);
      if (response.data.success) {
        addAlert({ type: "success", message: "Request sent successfully!" });
        onClose();
        setForm(initialForm);
      } else {
        addAlert({ type: "error", message: "Failed to send request. Please try again." });
      }
    } catch (error) {
      console.error("Error submitting contact request:", error);
      addAlert({ type: "error", message: "An error occurred. Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = 'unset';
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      {submitting && (
        <div
          style={{
            position: "fixed",
            zIndex: 2000,
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "#fff",
            display: "flex",
            flexDirection: "column", 
            alignItems: "center",
            justifyContent: "center",
          }}
        >
              <span style={{
      fontSize: "18px",
      color: "#222",
      marginBottom: "24px",
      fontWeight: 500,
      letterSpacing: 0.2,
    }}>
      Processing your Request.
    </span>
          <Loader height="70px" width="70px" />
        </div>
      )}
      <div 
        className="modal-overlay get-in-touch-modal"
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}
      >
        <div
          className="modal-content"
          onClick={e => e.stopPropagation()}
          style={{
            width: '393px',
            height: '680px',
            background: '#FFF',
            borderRadius: '20px',

          }}
        >
          <div className="modal-header">            <Box 
              style={{
                width: "64px",
                height: "67px",
                borderRadius: "99px",
                border: "3px solid #FFF",
                
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "#FFF"
              }}
            >              <Avatar
                url={profilePicture || ""}
                componentKey={`get-in-touch-${profileOwnerId}`}
                name={ownerName || "Profile Owner"}
                width="74px"
                height="74px"
              />
            </Box>
            <div className="header-text">
              <span>Send your Request to<br/>get started with your</span><br/>
              <span style={{ color: '#FF5043' }}>next project.</span>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="form-container">
              <div className="form-group" style={{  width: '331px', height: '73px' }}>
                <label>
                  Full Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  value={form.fullName}
                  onChange={handleField("fullName")}
                  placeholder="Enter your full name"
                  title="Full Name"
                />
              </div>

              <div className="form-group" style={{ width: '331px', height: '73px' }}>
                <label>
                  Contact Number <span className="required">*</span>
                </label>
                <input
                  type="tel"
                  value={form.contactNumber}
                  onChange={e => {
                    let value = e.target.value;
                    // Always enforce "+91-"
                    if (!value.startsWith("+91-")) {
                      // Remove any existing +91 or +91- and prepend the correct prefix
                      value = "+91-" + value.replace(/^(\+91-?)/, "");
                    }
                    // Prevent deleting the prefix
                    if (value.length < 4) {
                      value = "+91-";
                    }
                    setForm({ ...form, contactNumber: value });
                  }}
                  onKeyDown={e => {
                    // Prevent backspace/delete in the prefix
                    const input = e.currentTarget;
                    if (
                      (input.selectionStart ?? 0) <= 4 &&
                      (e.key === "Backspace" || e.key === "Delete")
                    ) {
                      e.preventDefault();
                    }
                  }}
                  placeholder="+91-XXXXXXXXXX"
                  maxLength={16}
              />
              </div>

              <div className="form-group" style={{ width: '331px', height: '73px' }}>
                <label>
                  Email ID <span className="required">*</span>
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={handleField("email")}
                  placeholder="Enter your email address"
                  title="Email ID"
                  style= {{ marginBottom: '8px' }}
                />
              </div>              <div className="form-group" style={{ width: '331px', height: '148px' }}>
                <label>Tell us about your requirement</label>
                <textarea 
                  value={form.requirement}
                  onChange={e => setForm({ ...form, requirement: e.target.value })}
                  placeholder="Describe your project requirements"
                  title="Tell us about your requirement"
                  style={{
                    textAlign: "left",
                    verticalAlign: "top",
                    lineHeight: "22px"
                  }}
                />
              </div>
            </div>

            <div className="button-group">
              <button
                type="submit"
                disabled={
                  submitting || 
                  !profileOwnerId || 
                  !form.fullName.trim() || 
                  !form.contactNumber.trim() || 
                  !form.email.trim() || 
                  !form.requirement.trim()
                }
                className="send-request-btn"
              >
                Send Request
              </button>
              <button 
                type="button"
                onClick={onClose}
                className="cancel-btn"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default GetInTouchModal;
